// @format

const POLL_INTERVAL_MS = 2000;
const SEARCH_INPUT_ID = "search-input";
const PANES_ID = "candidates-panes";
const TABLE_URL_FALLBACK = "/bots_prospects/table";

let bound = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
// the query whose rows are on screen (or being fetched)
let requestedQuery: string | null = null;
let inflight: AbortController | null = null;

const searchInput = (): HTMLInputElement | null =>
  document.getElementById(SEARCH_INPUT_ID) as HTMLInputElement | null;

const panes = (): HTMLElement | null => document.getElementById(PANES_ID);

// #search-input is shared with other pages (e.g. the inbox); only the
// candidates page renders the hidden `.bots-prospects-form`.
const onCandidatesPage = (): boolean => !!document.querySelector(".bots-prospects-form");

const currentValue = (): string => (searchInput()?.value || "").trim();

function stopPolling(): void {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

// The index URL for `query`: every other filter in the address bar is kept,
// the page goes back to 1 because the result set changed.
function indexParams(query: string): URLSearchParams {
  const params = new URLSearchParams(window.location.search);
  if (query) params.set("query", query);
  else params.delete("query");
  params.delete("page");
  return params;
}

function activeView(): string | null {
  return (
    document.querySelector<HTMLElement>("[data-viewtoggle] button[data-view].active")?.dataset.view ||
    panes()?.dataset.candidatesView ||
    null
  );
}

function tableUrl(params: URLSearchParams): string {
  const tableParams = new URLSearchParams(params);
  const view = activeView();
  if (view) tableParams.set("view", view);
  const base = panes()?.dataset.tableUrl || TABLE_URL_FALLBACK;
  return `${base}?${tableParams.toString()}`;
}

function setUrlParam(url: string, name: string, value: string): string {
  const parsed = new URL(url, window.location.origin);
  if (value) parsed.searchParams.set(name, value);
  else parsed.searchParams.delete(name);
  parsed.searchParams.delete("page");
  return `${parsed.pathname}${parsed.search}`;
}

// Keep every other control that carries the search in step with it, so a
// filter submit, CSV export or "Clear filters" acts on what is on screen.
function syncSearchControls(query: string): void {
  const hidden = document.querySelector<HTMLInputElement>('.bots-prospects-form input[name="query"]');
  if (hidden) hidden.value = query;

  const filterForm = document.querySelector<HTMLFormElement>("form[data-candidates-filter-form]");
  if (filterForm) {
    let field = filterForm.querySelector<HTMLInputElement>('input[name="query"]');
    if (!field && query) {
      field = document.createElement("input");
      field.type = "hidden";
      field.name = "query";
      filterForm.prepend(field);
    }
    if (field) {
      if (query) field.value = query;
      else field.remove();
    }
  }

  const exportBtn = document.getElementById("export-csv-btn");
  const exportUrl = exportBtn?.getAttribute("data-export-url");
  if (exportBtn && exportUrl) exportBtn.setAttribute("data-export-url", setUrlParam(exportUrl, "query", query));

  document.querySelectorAll<HTMLAnchorElement>("form[data-candidates-filter-form] a.cand-clear").forEach((link) => {
    link.setAttribute("href", setUrlParam(link.getAttribute("href") || "", "query", query));
  });
}

// The topbar's "X of Y loaded" is rendered with the page; refresh it from the
// counts the fresh table carries.
function syncLoadedCount(fresh: HTMLElement): void {
  const label = document.querySelector<HTMLElement>("[data-candidates-loaded-count]");
  const { loadedCount, totalCount } = fresh.dataset;
  if (!label || loadedCount == null || totalCount == null) return;

  const shown = label.querySelector<HTMLElement>("[data-page-shown]");
  const total = label.querySelector<HTMLElement>("[data-tab-total]");
  if (shown) {
    shown.dataset.offset = "0";
    shown.textContent = Number(loadedCount).toLocaleString("en-US");
  }
  if (total) total.textContent = Number(totalCount).toLocaleString("en-US");
}

function setBusy(busy: boolean): void {
  const root = panes();
  if (!root) return;
  root.toggleAttribute("aria-busy", busy);
  root.parentElement?.classList.toggle("is-searching", busy);
}

function fetchResults(query: string): void {
  const root = panes();
  // render_async has not delivered the first table yet — retry on the next tick
  if (!root) return;

  inflight?.abort();
  const controller = new AbortController();
  inflight = controller;
  requestedQuery = query;

  const params = indexParams(query);
  setBusy(true);

  fetch(tableUrl(params), {
    headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
    credentials: "same-origin",
    signal: controller.signal,
  })
    .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
    .then((html) => {
      const current = panes();
      const fresh = new DOMParser().parseFromString(html, "text/html").getElementById(PANES_ID);
      if (!current || !fresh) return;

      current.replaceWith(document.importNode(fresh, true));
      const search = params.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
      syncSearchControls(query);
      syncLoadedCount(fresh);
      // the same event the first async table load fires, so the new rows are
      // wired up (buttons, popovers, column order, view toggle) exactly alike
      document.dispatchEvent(new Event("render_async_load"));
    })
    .catch((err) => {
      if (controller.signal.aborted) return;
      // let the next tick (or keystroke) try this query again
      requestedQuery = null;
      console.error("Candidate search failed", err);
    })
    .finally(() => {
      if (inflight !== controller) return;
      inflight = null;
      setBusy(false);
    });
}

function poll(): void {
  if (!searchInput() || !onCandidatesPage()) {
    stopPolling();
    return;
  }

  const query = currentValue();
  if (query !== requestedQuery) {
    fetchResults(query);
  } else if (!inflight) {
    // value has settled and its rows are on screen
    stopPolling();
  }
}

function startPolling(): void {
  if (pollTimer) return;
  pollTimer = setInterval(poll, POLL_INTERVAL_MS);
}

export function initCandidateSearchAsYouType(): void {
  if (!onCandidatesPage()) return;

  // a fresh page (Turbo visit, filter submit) renders rows for the URL's query
  if (!pollTimer && !inflight) requestedQuery = currentValue();

  if (bound) return;
  bound = true;

  document.addEventListener("input", (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.id !== SEARCH_INPUT_ID || !onCandidatesPage()) return;
    startPolling();
  });

  document.addEventListener("keydown", (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.id !== SEARCH_INPUT_ID || !onCandidatesPage()) return;
    if (event.key !== "Enter" || event.isComposing) return;

    event.preventDefault();
    stopPolling();
    const query = currentValue();
    if (query !== requestedQuery) fetchResults(query);
  });

  document.addEventListener("turbo:before-visit", () => {
    stopPolling();
    inflight?.abort();
    inflight = null;
  });
}
